import React, {useCallback, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';

import {Button} from '@pb/kit-component-button';
import {Calendar} from '@pb/kit-component-calendar';
import {Group} from '@pb/kit-component-group';
import {Input} from '@pb/kit-component-input-v2';
import {Loading} from '@pb/kit-component-loading';
import {H2} from '@pb/kit-component-typography';

import {DateConfidence} from './services/reducer';
import {
    COUNTRY_OPTIONS,
    DATE_CONFIDENCE_OPTIONS,
    INFO_TYPE_OPTIONS,
    IS_REPLAY_AVAILABLE_OPTIONS,
    TIME_ZONE_OPTIONS,
    useEventDetails,
    VALIDATION_SOURCE_OPTIONS,
} from './useEventDetails';

import {
    clearButton,
    companySearchDropdown,
    companySearchEmpty,
    companySearchItem,
    companySearchItemId,
    companySearchItemName,
    companySearchLoading as companySearchLoadingStyle,
    companySearchWrapper,
    dialInGroup,
    dialInInput,
    dialInLabel,
    dialInRow,
    errorMessage,
    eventDetailsContainer,
    fileUploadRow,
    formActions,
    formCard,
    formHeaderTitle,
    formInput,
    formInputSmall,
    formLabel,
    formRow,
    formSelect,
    formSelectSmall,
    formValue,
    leftColumn,
    notesArea,
    openLink,
    quarterButtons,
    radioGroup,
    readonlyField,
    rightColumn,
    sectionDivider,
    sectionTitle,
    successMessage,
    twoColumnLayout,
} from './event-details.styles.module.styl';

// ──────── Shared inline styles ────────

const selectStyle: React.CSSProperties = {
    padding: '4px 6px',
    fontSize: 12,
    border: '1px solid #ccc',
    borderRadius: 3,
    width: '100%',
};

// ──────── Helpers ────────

const parseCalendarDate = (dateStr: string): Date | null => {
    if (!dateStr) return null;
    try {
        const d = new Date(dateStr);
        return isNaN(d.getTime()) ? null : d;
    } catch {
        return null;
    }
};

const formatDateForStore = (date: Date | null): string => {
    if (!date) return '';
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
};

// ──────── Quarter Buttons ────────

const QUARTER_BUTTONS = ['Q1', 'Q2', 'Q3', 'Q4', 'AGM', 'Annual'] as const;

// ──────── Component ────────

const EventDetailsView: React.FC = () => {
    const {
        formData,
        saving,
        error,
        successMessage: saveSuccess,
        setField,
        handleSave,
        handleReset,
        EVENT_TYPE_OPTIONS,
        STATUS_OPTIONS,
        companySearchResults,
        companySearchLoading,
        searchCompanies,
        selectCompany,
        clearCompanyResults,
        eventDetailLoading,
        editingEventId,
        eventTypesLoading,
        statusesLoading,
        eventId,
    } = useEventDetails();

    const fileInputRef = useRef<HTMLInputElement>(null);
    const [companyQuery, setCompanyQuery] = useState('');
    const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
    const companyDropdownRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();

    const handleBack = useCallback(() => {
        handleReset();
        navigate('/manage-event-calendar');
    }, [handleReset, navigate]);

    // ──── Handlers ────

    const handleQuarterClick = useCallback((quarter: string) => {
        // Map quarter button to event type from API response
        const quarterKeywords: Record<string, string[]> = {
            Q1: ['First Quarter', 'Q1'],
            Q2: ['Second Quarter', 'Q2'],
            Q3: ['Third Quarter', 'Q3'],
            Q4: ['Fourth Quarter', 'Q4'],
            AGM: ['Annual General', 'AGM'],
            Annual: ['Annual'],
        };
        
        const keywords = quarterKeywords[quarter] || [];
        const matchedEventType = EVENT_TYPE_OPTIONS.find(opt => 
            keywords.some(kw => opt.label.includes(kw))
        );
        
        if (matchedEventType?.value) {
            setField('eventType', matchedEventType.value);
            // Auto-bind Event Name = Company Name + Event Type label
            if (formData.companyName) {
                setField('eventName', `${formData.companyName} ${matchedEventType.label}`);
            }
        }
    }, [setField, formData.companyName, EVENT_TYPE_OPTIONS]);

    const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] ?? null;
        setField('screenshotFile', file as unknown as string);
    }, [setField]);

    const handleRemoveFile = useCallback(() => {
        setField('screenshotFile', null as unknown as string);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }, [setField]);

    const handleClearDialIn = useCallback(() => {
        setField('tollFree', '');
        setField('tollNumber', '');
        setField('passCode', '');
        setField('replayTollFree', '');
        setField('replayTollNumber', '');
        setField('replayPassCode', '');
    }, [setField]);

    const handleOpenUrl = useCallback((url: string) => {
        if (url) {
            window.open(url.startsWith('http') ? url : `https://${url}`, '_blank', 'noopener');
        }
    }, []);

    // Show loader while: saving, any API is in-flight, dropdowns haven't loaded yet,
    // or we have an eventId to fetch but the fetch hasn't completed yet.
    const dropdownsPending = EVENT_TYPE_OPTIONS.length <= 1 || STATUS_OPTIONS.length <= 1;
    const eventFetchPending = !!eventId && editingEventId !== eventId;
    const pageLoading = saving || eventDetailLoading || eventTypesLoading || statusesLoading || dropdownsPending || eventFetchPending;

    if (pageLoading) {
        return (
            <Group style={{justifyContent: 'center', padding: 60}}>
                <Loading/>
            </Group>
        );
    }

    return (
        <div className={eventDetailsContainer}>
            <div className={formCard}>
                {/* Header */}
                <div className={formHeaderTitle} style={{marginBottom: 10}}>
                    {editingEventId ? `Update Event Details (Event ID: ${editingEventId})` : 'Add Event Details'}
                </div>

                {/* Messages */}
                {error && <div className={errorMessage}>{error}</div>}
                {saveSuccess && <div className={successMessage}>{saveSuccess}</div>}

                <div className={twoColumnLayout}>
                    {/* ════════ LEFT COLUMN ════════ */}
                    <div className={leftColumn}>
                        {/* Company */}
                        <div className={formRow}>
                            <label className={formLabel}>Company</label>
                            <div className={formValue}>
                                <div className={companySearchWrapper}>
                                    <input
                                        type="text"
                                        style={{
                                            padding: '4px 6px',
                                            fontSize: 12,
                                            border: '1px solid #ccc',
                                            borderRadius: 3,
                                            width: '100%',
                                        }}
                                        value={formData.companyId ? `(${formData.companyId}) ${formData.companyName}` : companyQuery}
                                        onChange={e => {
                                            const val = e.target.value;
                                            // If a company was already selected, clear it when user starts typing
                                            if (formData.companyId) {
                                                setField('companyId', '');
                                                setField('companyName', '');
                                                setField('fiscalYearEnd', '');
                                            }
                                            setCompanyQuery(val);
                                            setShowCompanyDropdown(false);
                                        }}
                                        onFocus={() => {
                                            if (companySearchResults.length > 0) {
                                                setShowCompanyDropdown(true);
                                            }
                                        }}
                                        onBlur={() => {
                                            const query = companyQuery.trim();
                                            if (query && !formData.companyId) {
                                                const field = /^[0-9A-Z]+$/i.test(query) && query.length >= 2 ? 'COMPANY_ID' as const : 'COMPANY_NAME' as const;
                                                searchCompanies(query, field);
                                                setShowCompanyDropdown(true);
                                            } else {
                                                // Delay to allow click on dropdown item
                                                setTimeout(() => setShowCompanyDropdown(false), 200);
                                            }
                                        }}
                                        placeholder="Search company..."
                                    />
                                    {showCompanyDropdown && (companySearchLoading || companySearchResults.length > 0 || (companyQuery.length > 0 && !companySearchLoading)) && (
                                        <div className={companySearchDropdown} ref={companyDropdownRef}>
                                            {companySearchLoading && (
                                                <div className={companySearchLoadingStyle}>Searching...</div>
                                            )}
                                            {!companySearchLoading && companySearchResults.length === 0 && companyQuery.length > 0 && (
                                                <div className={companySearchEmpty}>No companies found</div>
                                            )}
                                            {!companySearchLoading && companySearchResults.map(company => (
                                                <div
                                                    key={company.companyId}
                                                    className={companySearchItem}
                                                    onMouseDown={e => {
                                                        e.preventDefault();
                                                        selectCompany(company);
                                                        setCompanyQuery('');
                                                        setShowCompanyDropdown(false);
                                                    }}
                                                >
                                                    <span className={companySearchItemId}>({company.companyId})</span>
                                                    <span className={companySearchItemName}>{company.companyName}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Quarter Buttons */}
                        <div className={quarterButtons}>
                            {QUARTER_BUTTONS.map(q => (
                                <Button key={q} size="small" onClick={() => handleQuarterClick(q)}>{q}</Button>
                            ))}
                            <Button size="small" onClick={() => { /* Update logic */ }}>Update</Button>
                        </div>

                        {/* Event Name */}
                        <div className={formRow}>
                            <label className={formLabel}>Event Name</label>
                            <div className={formValue}>
                                <input
                                    type="text"
                                    style={{
                                        padding: '4px 6px',
                                        fontSize: 12,
                                        border: '1px solid #ccc',
                                        borderRadius: 3,
                                        width: '100%',
                                        boxSizing: 'border-box',
                                    }}
                                    value={formData.eventName}
                                    onChange={e => setField('eventName', e.target.value)}
                                    placeholder="Enter event name"
                                />
                            </div>
                        </div>

                        {/* Spell Check + Repeatable Event */}
                        <div className={formRow}>
                            <label className={formLabel}/>
                            <div className={formValue} style={{gap: 12}}>
                                <Button size="small" onClick={() => { /* Spell check logic */ }}>Spell Check</Button>
                                <label style={{display: 'flex', alignItems: 'center', gap: 3, fontSize: 12, whiteSpace: 'nowrap'}}>
                                    <input type="checkbox" /> Repeatable Event
                                </label>
                            </div>
                        </div>

                        {/* Event Standard Name */}
                        <div className={formRow}>
                            <label className={formLabel}>Event Standard Name</label>
                            <div className={formValue}>
                                <span className={readonlyField}>{formData.eventStandardName || '—'}</span>
                            </div>
                        </div>

                        {/* Event Type */}
                        <div className={formRow}>
                            <label className={formLabel}>Event Type</label>
                            <div className={formValue}>
                                <select
                                    className={formSelect}
                                    style={selectStyle}
                                    value={formData.eventType}
                                    onChange={e => {
                                        const selectedValue = e.target.value;
                                        setField('eventType', selectedValue);
                                        // Auto-bind Event Name = Company Name + Event Type label
                                        const selectedLabel = EVENT_TYPE_OPTIONS.find(opt => opt.value === selectedValue)?.label ?? '';
                                        if (selectedValue && formData.companyName) {
                                            setField('eventName', `${formData.companyName} ${selectedLabel}`);
                                        }
                                    }}
                                >
                                    {EVENT_TYPE_OPTIONS.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Effective Beginning Date */}
                        <div className={formRow}>
                            <label className={formLabel}>Effective Beginning Date</label>
                            <div className={formValue}>
                                <div style={{flexShrink: 0}}>
                                    <Calendar
                                        selected={parseCalendarDate(formData.effectiveBeginDate)}
                                        onChange={date => setField('effectiveBeginDate', formatDateForStore(date))}
                                    />
                                </div>
                                <span style={{fontSize: 12, whiteSpace: 'nowrap', flexShrink: 0}}>Time:</span>
                                <input
                                    type="text"
                                    className={formInputSmall}
                                    style={{
                                        padding: '4px 6px',
                                        fontSize: 12,
                                        border: '1px solid #ccc',
                                        borderRadius: 3,
                                        boxSizing: 'border-box',
                                    }}
                                    value={formData.effectiveBeginTime}
                                    onChange={e => setField('effectiveBeginTime', e.target.value)}
                                    placeholder="HH:MM AM"
                                />
                            </div>
                        </div>

                        {/* Date Confidence (Radio) */}
                        <div className={formRow} style={{marginTop: 4}}>
                            <label className={formLabel}/>
                            <div className={radioGroup}>
                                {DATE_CONFIDENCE_OPTIONS.map(opt => (
                                    <label key={opt.value} style={{display: 'flex', alignItems: 'center', gap: 3, fontSize: 12, cursor: 'pointer'}}>
                                        <input
                                            type="radio"
                                            name="dateConfidence"
                                            value={opt.value}
                                            checked={formData.dateConfidence === opt.value}
                                            onChange={() => setField('dateConfidence', opt.value as DateConfidence)}
                                        />
                                        {opt.label}
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Effective End Date */}
                        <div className={formRow} style={{marginTop: 4}}>
                            <label className={formLabel}>Effective End Date</label>
                            <div className={formValue}>
                                <div style={{flexShrink: 0}}>
                                    <Calendar
                                        selected={parseCalendarDate(formData.effectiveEndDate)}
                                        onChange={date => setField('effectiveEndDate', formatDateForStore(date))}
                                    />
                                </div>
                                <span style={{fontSize: 12, whiteSpace: 'nowrap', flexShrink: 0}}>Time:</span>
                                <input
                                    type="text"
                                    className={formInputSmall}
                                    style={{
                                        padding: '4px 6px',
                                        fontSize: 12,
                                        border: '1px solid #ccc',
                                        borderRadius: 3,
                                        boxSizing: 'border-box',
                                    }}
                                    value={formData.effectiveEndTime}
                                    onChange={e => setField('effectiveEndTime', e.target.value)}
                                    placeholder="HH:MM"
                                />
                            </div>
                        </div>

                        {/* Fiscal Year End */}
                        <div className={formRow}>
                            <label className={formLabel}>Fiscal Year End</label>
                            <div className={formValue}>
                                <span className={readonlyField}>{formData.fiscalYearEnd || '—'}</span>
                            </div>
                        </div>

                        {/* Event Year */}
                        <div className={formRow}>
                            <label className={formLabel}>Event Year</label>
                            <div className={formValue}>
                                <Input
                                    className={formInputSmall}
                                    value={formData.eventYear}
                                    onChange={val => setField('eventYear', val)}
                                    placeholder="YYYY"
                                />
                            </div>
                        </div>

                        {/* Event Detail URL */}
                        <div className={formRow}>
                            <label className={formLabel}>Event Detail Url</label>
                            <div className={formValue}>
                                <input
                                    type="text"
                                    style={{
                                        padding: '4px 6px',
                                        fontSize: 12,
                                        border: '1px solid #ccc',
                                        borderRadius: 3,
                                        width: '100%',
                                        flex: 1,
                                        minWidth: 0,
                                        boxSizing: 'border-box',
                                    }}
                                    value={formData.eventDetailUrl}
                                    onChange={e => setField('eventDetailUrl', e.target.value)}
                                    placeholder="https://..."
                                />
                                <span className={openLink} onClick={() => handleOpenUrl(formData.eventDetailUrl)}>Open</span>
                            </div>
                        </div>

                        {/* Webcast URL */}
                        <div className={formRow}>
                            <label className={formLabel}>Webcast Url</label>
                            <div className={formValue}>
                                <input
                                    type="text"
                                    style={{
                                        padding: '4px 6px',
                                        fontSize: 12,
                                        border: '1px solid #ccc',
                                        borderRadius: 3,
                                        width: '100%',
                                        flex: 1,
                                        minWidth: 0,
                                        boxSizing: 'border-box',
                                    }}
                                    value={formData.webcastUrl}
                                    onChange={e => setField('webcastUrl', e.target.value)}
                                    placeholder="https://..."
                                />
                                <span className={openLink} onClick={() => handleOpenUrl(formData.webcastUrl)}>Open</span>
                            </div>
                        </div>

                        {/* IsReplay Available */}
                        <div className={formRow}>
                            <label className={formLabel}>IsReplay Available:</label>
                            <div className={formValue}>
                                <select
                                    className={formSelect}
                                    style={selectStyle}
                                    value={formData.isReplayAvailable}
                                    onChange={e => setField('isReplayAvailable', e.target.value)}
                                >
                                    {IS_REPLAY_AVAILABLE_OPTIONS.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Replay Expiry Date */}
                        <div className={formRow}>
                            <label className={formLabel}>Replay Expiry Date</label>
                            <div className={formValue}>
                                <div style={{flexShrink: 0}}>
                                    <Calendar
                                        selected={parseCalendarDate(formData.replayExpiryDate)}
                                        onChange={date => setField('replayExpiryDate', formatDateForStore(date))}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ════════ RIGHT COLUMN ════════ */}
                    <div className={rightColumn}>
                        {/* Dial-In Details Section */}
                        <div className={sectionDivider} style={{borderTop: 'none', marginTop: 0}}>
                            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                <span className={sectionTitle}>Dial-In Details:-</span>
                                <button className={clearButton} onClick={handleClearDialIn} type="button">Clear</button>
                            </div>

                            <div className={dialInGroup} style={{paddingLeft: 0}}>
                                <div className={dialInRow}>
                                    <span className={dialInLabel}>Toll Free:</span>
                                    <Input
                                        className={dialInInput}
                                        style={{width: '100%'}}
                                        value={formData.tollFree}
                                        onChange={val => setField('tollFree', val)}
                                    />
                                </div>
                                <div className={dialInRow}>
                                    <span className={dialInLabel}>Toll Number</span>
                                    <Input
                                        className={dialInInput}
                                        style={{width: '100%'}}
                                        value={formData.tollNumber}
                                        onChange={val => setField('tollNumber', val)}
                                    />
                                </div>
                                <div className={dialInRow}>
                                    <span className={dialInLabel}>Pass Code:</span>
                                    <Input
                                        className={dialInInput}
                                        style={{width: '100%'}}
                                        value={formData.passCode}
                                        onChange={val => setField('passCode', val)}
                                    />
                                </div>
                                <div className={dialInRow}>
                                    <span className={dialInLabel}>R-Toll Free:</span>
                                    <Input
                                        className={dialInInput}
                                        style={{width: '100%'}}
                                        value={formData.replayTollFree}
                                        onChange={val => setField('replayTollFree', val)}
                                    />
                                </div>
                                <div className={dialInRow}>
                                    <span className={dialInLabel}>R-Toll Numbers:</span>
                                    <Input
                                        className={dialInInput}
                                        style={{width: '100%'}}
                                        value={formData.replayTollNumber}
                                        onChange={val => setField('replayTollNumber', val)}
                                    />
                                </div>
                                <div className={dialInRow}>
                                    <span className={dialInLabel}>R-Pass Code:</span>
                                    <Input
                                        className={dialInInput}
                                        style={{width: '100%'}}
                                        value={formData.replayPassCode}
                                        onChange={val => setField('replayPassCode', val)}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Country */}
                        <div className={formRow}>
                            <label className={formLabel}>Country</label>
                            <div className={formValue}>
                                <select
                                    className={formSelect}
                                    style={selectStyle}
                                    value={formData.country}
                                    onChange={e => setField('country', e.target.value)}
                                >
                                    {COUNTRY_OPTIONS.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Time Zone */}
                        <div className={formRow}>
                            <label className={formLabel}>Time Zone</label>
                            <div className={formValue}>
                                <select
                                    className={formSelect}
                                    style={selectStyle}
                                    value={formData.timeZone}
                                    onChange={e => setField('timeZone', e.target.value)}
                                >
                                    {TIME_ZONE_OPTIONS.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Validation Source */}
                        <div className={formRow}>
                            <label className={formLabel}>Validation Source</label>
                            <div className={formValue}>
                                <select
                                    className={formSelectSmall}
                                    style={selectStyle}
                                    value={formData.validationSource}
                                    onChange={e => setField('validationSource', e.target.value)}
                                >
                                    {VALIDATION_SOURCE_OPTIONS.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Info Type */}
                        <div className={formRow}>
                            <label className={formLabel}>Info Type</label>
                            <div className={formValue}>
                                <select
                                    className={formSelectSmall}
                                    style={selectStyle}
                                    value={formData.infoType}
                                    onChange={e => setField('infoType', e.target.value)}
                                >
                                    {INFO_TYPE_OPTIONS.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Status */}
                        <div className={formRow}>
                            <label className={formLabel}>Status</label>
                            <div className={formValue}>
                                <select
                                    className={formSelectSmall}
                                    style={selectStyle}
                                    value={formData.status}
                                    onChange={e => setField('status', e.target.value)}
                                >
                                    {STATUS_OPTIONS.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Notes */}
                        <div className={formRow}>
                            <label className={formLabel}>Notes</label>
                            <div className={formValue}>
                                <textarea
                                    className={notesArea}
                                    value={formData.notes}
                                    onChange={e => setField('notes', e.target.value)}
                                    rows={2}
                                    placeholder="Enter notes..."
                                />
                            </div>
                        </div>

                        {/* Screenshot */}
                        <div className={formRow}>
                            <label className={formLabel}>Screenshot</label>
                            <div className={fileUploadRow}>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={handleFileChange}
                                />
                                {formData.screenshotFile && (
                                    <>
                                        <span className={openLink}>View</span>
                                        <span
                                            className={openLink}
                                            style={{color: '#cc3300'}}
                                            onClick={handleRemoveFile}
                                        >
                                            Remove
                                        </span>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Creation Date */}
                        <div className={formRow}>
                            <label className={formLabel}>Creation Date</label>
                            <div className={formValue}>
                                <span className={readonlyField}>{formData.creationDate}</span>
                            </div>
                        </div>

                        {/* Holiday Remark */}
                        <div className={formRow}>
                            <label className={formLabel}>Holiday Remark</label>
                            <div className={formValue}>
                                <Input
                                    className={formInput}
                                    style={{width: '100%'}}
                                    value={formData.holidayRemark}
                                    onChange={val => setField('holidayRemark', val)}
                                    placeholder="Enter holiday remark"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Action Buttons — full width below both columns */}
                <div className={formActions} style={{display: 'flex', gap: 12}}>
                    <Button onClick={handleBack} kind="secondary">Back</Button>
                    <Button onClick={handleSave} disabled={saving}>Save Details</Button>
                </div>
            </div>
        </div>
    );
};

export default EventDetailsView;
